import { ManufactureRequestsActions } from './actions';
import { ManufactureRequestModel, ManufactureRequestLineModel } from './model';
import { createReducer, on } from '@ngrx/store';

export enum ManufactureRequestModalStatus {
  Init = 0,
  Called = 1,
  Success = 2,
  Failure = 3,
}

export enum ManufactureRequestModalType {
  Create = 0,
  Edit = 1,
  View = 2,
}

const {
  getManufactureRequests,
  getManufactureRequestsFailure,
  getManufactureRequestsSuccess,
  getManufactureRequest,
  getManufactureRequestFailure,
  getManufactureRequestSuccess,
  createManufactureRequest,
  createManufactureRequestFailure,
  createManufactureRequestSuccess,
  updateManufactureRequest,
  updateManufactureRequestFailure,
  updateManufactureRequestSuccess,
  deleteManufactureRequest,
  deleteManufactureRequestFailure,
  deleteManufactureRequestSuccess,
  openManufactureRequestView,
  cleanProductInManufactureRequest,
  addProductInManufactureRequest,
  editProductInManufactureRequest,
  removeProductInManufactureRequest,
} = ManufactureRequestsActions;
export interface ManufactureRequestState {
  list: Array<ManufactureRequestModel>;
  item: ManufactureRequestModel;
  pagination: {
    page: number;
    size: number;
    totalRecords: number;
  };
  params: {
    page?: number;
    size?: number;
    sort?: string;
    search?: string;
    name?: string;
    projectId?: number;
  };
  loading: boolean;
  modal: {
    type: ManufactureRequestModalType;
    data: any;
    isLoading: boolean;
    errors: Array<any>;
    status: ManufactureRequestModalStatus;
  };
  inProducts: Array<ManufactureRequestLineModel>;
}

export const initialState: ManufactureRequestState = {
  list: [],
  item: null,
  pagination: {
    page: 0,
    size: 10,
    totalRecords: 0,
  },
  params: {
    page: 0,
    size: 10,
    sort: null,
    search: null,
    name: null,
    projectId: null,
  },
  loading: false,
  modal: {
    type: ManufactureRequestModalType.View,
    data: null,
    isLoading: false,
    errors: [],
    status: ManufactureRequestModalStatus.Init,
  },
  inProducts: [],
};

export const reducer = createReducer(
  initialState,
  on(getManufactureRequests, (state, { payload }) => {
    return { ...state, loading: true, params: { ...payload } };
  }),
  on(getManufactureRequestsSuccess, (state, { payload }) => {
    return {
      ...state,
      list: [...payload.data],
      pagination: { ...payload.pagination },
      loading: false,
    };
  }),
  on(getManufactureRequestsFailure, (state) => {
    return {
      ...state,
      loading: false,
    };
  }),
  on(getManufactureRequest, (state, { payload }) => {
    return { ...state, loading: true };
  }),
  on(getManufactureRequestSuccess, (state, { payload }) => {
    return {
      ...state,
      item: { ...payload.data },
      loading: false,
    };
  }),
  on(getManufactureRequestFailure, (state) => {
    return {
      ...state,
      loading: false,
    };
  }),
  on(updateManufactureRequest, (state) => {
    const modal = {
      ...state.modal,
      isLoading: true,
      status: ManufactureRequestModalStatus.Called,
      type: ManufactureRequestModalType.Edit,
      errors: [],
    };
    return {
      ...state,
      modal,
    };
  }),
  on(updateManufactureRequestSuccess, (state) => {
    const modal = {
      ...state.modal,
      isLoading: false,
      status: ManufactureRequestModalStatus.Success,
    };
    return {
      ...state,
      modal,
    };
  }),
  on(updateManufactureRequestFailure, (state, { payload }) => {
    const { validationErrors = [] } = payload;
    const modal = {
      ...state.modal,
      isLoading: false,
      status: ManufactureRequestModalStatus.Failure,
      errors: [...validationErrors],
    };
    return {
      ...state,
      modal,
    };
  }),
  on(createManufactureRequest, (state, { payload }) => {
    const modal = {
      ...state.modal,
      isLoading: true,
      status: ManufactureRequestModalStatus.Init,
      type: ManufactureRequestModalType.Create,
      errors: [],
    };
    return {
      ...state,
      modal,
    };
  }),
  on(createManufactureRequestSuccess, (state) => {
    const modal = {
      ...state.modal,
      isLoading: false,
      status: ManufactureRequestModalStatus.Success,
    };
    return {
      ...state,
      modal,
    };
  }),
  on(createManufactureRequestFailure, (state, { payload }) => {
    const { validationErrors = [] } = payload;
    const modal = {
      ...state.modal,
      isLoading: false,
      status: ManufactureRequestModalStatus.Failure,
      errors: [...validationErrors],
    };
    return {
      ...state,
      modal,
    };
  }),
  on(deleteManufactureRequest, (state) => {
    return { ...state, loading: true };
  }),
  on(deleteManufactureRequestSuccess, (state) => {
    return { ...state, loading: false };
  }),
  on(deleteManufactureRequestFailure, (state) => {
    return { ...state, loading: false };
  }),
  on(openManufactureRequestView, (state, { payload }) => {
    const modal = {
      ...state.modal,
      status: ManufactureRequestModalStatus.Init,
      errors: [],
    };

    if (payload && Array.isArray(payload.inProducts)) {
      const lines = payload.inProducts;
      const inProducts = lines.map((c) => {
        return {
          ...c,
          uid: `server-${c.id}`,
          name: c.productName,
          note: c.note,
          unit: c.productUnit,
        };
      });
      return {
        ...state,
        modal,
        inProducts,
      };
    }

    return {
      ...state,
      modal,
      inProducts: [],
    };
  }),
  on(addProductInManufactureRequest, (state, { payload }) => {
    const { product, quantity } = payload;
    const length = state.inProducts.length + 1;
    const productExist = state.inProducts.find(
      (p) => p.productId === product.id
    );
    if (productExist) {
      const inProducts = state.inProducts.map((p) => {
        let qty = quantity;
        if (p.productId === product.id) {
          qty += p.quantity;
        }
        return { ...p, quantity: qty, isEdited: true };
      });

      return {
        ...state,
        inProducts,
      };
    }
    const inProduct = {
      uid: `local-${length}`,
      name: product.name,
      productId: product.id,
      quantity,
      isNew: true,
    } as ManufactureRequestLineModel;

    return {
      ...state,
      inProducts: [...state.inProducts, inProduct],
    };
  }),
  on(editProductInManufactureRequest, (state, { payload }) => {
    const { product, quantity } = payload;
    const newProducts = state.inProducts.map((c) => {
      if (c.productId === product.productId && !c.isNew) {
        return {
          ...c,
          ...product,
          productId: product.productId,
          quantity,
          isEdited: true,
        };
      }
      if (c.productId === product.productId && c.isNew) {
        return { ...c, ...product, productId: product.productId };
      }
      return c;
    });
    return {
      ...state,
      inProducts: newProducts,
    };
  }),
  on(removeProductInManufactureRequest, (state, { payload }) => {
    if (!payload) {
      return {
        ...state,
      };
    }
    const { product } = payload;
    const newProducts = state.inProducts
      .map((c) => {
        if (c.productId === product.productId) {
          return { ...c, isRemoved: true };
        }
        return c;
      })
      .filter((c) => {
        if (c.productId === product.productId && c.isNew) {
          return false;
        }
        return true;
      });
    return {
      ...state,
      inProducts: newProducts,
    };
  }),
  on(cleanProductInManufactureRequest, (state) => {
    return {
      ...state,
      inProducts: [],
    };
  })
);
